import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  CaptureDfPayload,
  CreateDebtorPayload,
  CreateDfPayload,
  CreateInvoicePayload,
  InsuranceStatusPayload,
  DebtorRelationship,
  DfFacilityDetail,
  DfFacilityDocument,
  DfFacilityOps,
  DfFacilitySubmitResponse,
  FacilitiesResponse,
  FacilityDecisionPayload,
  FundInvoicePayload,
  Invoice,
  InvoiceDetail,
  InvoiceOps,
} from './df.types';
import { OpsEmailLogEntry } from '../status/email-log.types';
import { SizingHoldReleasePayload } from '../applications/sizing-hold.types';

interface DebtorsResponse {
  debtors: DebtorRelationship[];
}

interface InvoicesResponse {
  invoices: Invoice[];
}

interface DfOpsInvoicesResponse {
  invoices: InvoiceOps[];
}

interface DfFacilitiesOpsResponse {
  facilities: DfFacilityOps[];
}

interface DfAssessResponse {
  log_short_id: string;
  status: string;
  summary: string;
}

@Injectable({ providedIn: 'root' })
export class DfService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/df`;

  // -------------------------------------------------------------------------
  // Client — facility
  // -------------------------------------------------------------------------

  listFacilitiesForEntity(entityShortId: string): Observable<FacilitiesResponse> {
    return this.http.get<FacilitiesResponse>(`${this.base}/facilities/`, {
      params: { entity: entityShortId },
    });
  }

  getFacility(shortId: string): Observable<DfFacilityDetail> {
    return this.http.get<DfFacilityDetail>(`${this.base}/facilities/${shortId}/`);
  }

  createFacility(payload: CreateDfPayload): Observable<DfFacilityDetail> {
    return this.http.post<DfFacilityDetail>(`${this.base}/facilities/`, payload);
  }

  /** Soft-delete (withdraw) a DRAFT facility. Backend rejects non-DRAFT stages. */
  withdrawFacility(shortId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/facilities/${shortId}/`);
  }

  captureFacility(shortId: string, payload: CaptureDfPayload): Observable<DfFacilityDetail> {
    return this.http.patch<DfFacilityDetail>(
      `${this.base}/facilities/${shortId}/capture/`,
      payload,
    );
  }

  uploadFacilityDocument(
    shortId: string,
    file: File,
    docType?: string,
    label?: string,
  ): Observable<DfFacilityDocument> {
    const fd = new FormData();
    fd.append('file', file);
    if (docType) {
      fd.append('doc_type', docType);
    }
    if (label) {
      fd.append('label', label);
    }
    return this.http.post<DfFacilityDocument>(`${this.base}/facilities/${shortId}/documents/`, fd);
  }

  /**
   * Soft-delete a facility document the applicant uploaded by mistake.
   * Backend allows this only on DRAFT / MORE_INFO_REQUIRED facilities.
   */
  deleteFacilityDocument(shortId: string, docShortId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/facilities/${shortId}/documents/${docShortId}/`);
  }

  submitFacility(
    shortId: string,
    aiProcessingConsent: boolean,
  ): Observable<DfFacilitySubmitResponse> {
    return this.http.post<DfFacilitySubmitResponse>(`${this.base}/facilities/${shortId}/submit/`, {
      ai_processing_consent: aiProcessingConsent,
    });
  }

  /**
   * Withdraw the AI-processing consent on this facility (FLOW-290, POPIA s11(2)).
   *
   * The act behind the "You can withdraw consent at any time" promise. Not an
   * update of the consent row: the server transitions it and every AI gate
   * reads the result live, so the next assessment is refused immediately.
   * Re-consenting goes back through the existing consent path — it writes a new
   * row rather than reviving this one. A second withdrawal is a 404
   * `NO_ACTIVE_CONSENT`.
   *
   * `reason` is optional and never shown back: the right is unqualified, so the
   * UI does not ask a client to justify it. Ops passes one when keying a
   * withdrawal received by phone or email.
   */
  withdrawAiConsent(shortId: string, reason?: string): Observable<DfFacilityDetail> {
    return this.http.post<DfFacilityDetail>(
      `${this.base}/facilities/${shortId}/ai-consent/withdraw/`,
      reason ? { reason } : {},
    );
  }

  respondFacility(shortId: string): Observable<DfFacilityDetail> {
    return this.http.post<DfFacilityDetail>(`${this.base}/facilities/${shortId}/respond/`, {});
  }

  /**
   * BRE-200 DF-OB-06 (FLOW-163) — the client signs the facility agreement.
   *
   * A gating condition for activation: until this is confirmed (or Ops records
   * a waiver), the facility cannot be activated. No body — the POST IS the
   * signature — and idempotent server-side.
   */
  confirmDfAgreement(shortId: string): Observable<DfFacilityDetail> {
    return this.http.post<DfFacilityDetail>(
      `${this.base}/facilities/${shortId}/confirm-agreement/`,
      {},
    );
  }

  // -------------------------------------------------------------------------
  // Client — debtors
  // -------------------------------------------------------------------------

  getDebtors(facilityShortId: string): Observable<DebtorsResponse> {
    return this.http.get<DebtorsResponse>(`${this.base}/facilities/${facilityShortId}/debtors/`);
  }

  /**
   * POST to facilities/<short_id>/debtors/ with {name, reg_no?}.
   * The backend creates both the debtor entity and the relationship in one call.
   */
  createDebtor(
    facilityShortId: string,
    payload: CreateDebtorPayload,
  ): Observable<DebtorRelationship> {
    return this.http.post<DebtorRelationship>(
      `${this.base}/facilities/${facilityShortId}/debtors/`,
      payload,
    );
  }

  // -------------------------------------------------------------------------
  // Client — invoices
  // -------------------------------------------------------------------------

  getInvoices(facilityShortId: string): Observable<InvoicesResponse> {
    return this.http.get<InvoicesResponse>(`${this.base}/facilities/${facilityShortId}/invoices/`);
  }

  /** Multipart: the invoice's fields and the invoice itself (§C5.1 gate ten). */
  createInvoice(
    facilityShortId: string,
    payload: CreateInvoicePayload,
    file: File,
  ): Observable<InvoiceDetail> {
    const body = new FormData();
    for (const [key, value] of Object.entries(payload)) {
      if (value !== undefined) body.append(key, value);
    }
    body.append('file', file);
    return this.http.post<InvoiceDetail>(
      `${this.base}/facilities/${facilityShortId}/invoices/`,
      body,
    );
  }

  opsSetInsuranceStatus(
    facilityShortId: string,
    payload: InsuranceStatusPayload,
  ): Observable<DfFacilityOps> {
    return this.http.post<DfFacilityOps>(
      `${this.base}/ops/facilities/${facilityShortId}/insurance/`,
      payload,
    );
  }

  // -------------------------------------------------------------------------
  // Ops — facilities
  // -------------------------------------------------------------------------

  opsList(params?: {
    df_status?: string;
    application_stage?: string;
  }): Observable<DfFacilitiesOpsResponse> {
    let httpParams = new HttpParams();
    if (params?.df_status) {
      httpParams = httpParams.set('df_status', params.df_status);
    }
    if (params?.application_stage) {
      httpParams = httpParams.set('application_stage', params.application_stage);
    }
    return this.http.get<DfFacilitiesOpsResponse>(`${this.base}/ops/facilities/`, {
      params: httpParams,
    });
  }

  opsGetFacility(shortId: string): Observable<DfFacilityOps> {
    return this.http.get<DfFacilityOps>(`${this.base}/ops/facilities/${shortId}/`);
  }

  opsFacilityDecision(
    shortId: string,
    payload: FacilityDecisionPayload,
  ): Observable<DfFacilityOps> {
    return this.http.post<DfFacilityOps>(
      `${this.base}/ops/facilities/${shortId}/decision/`,
      payload,
    );
  }

  opsFacilityAssess(shortId: string): Observable<DfAssessResponse> {
    return this.http.post<DfAssessResponse>(`${this.base}/ops/facilities/${shortId}/assess/`, {});
  }

  /**
   * BRE-200 DF-OB-07 (FLOW-163) — Ops confirms cession of the debtor book /
   * TPFA. Ops-only by rule: the condition also requires that existing cessions
   * to other funders are cancelled or waived first, which nothing in the schema
   * models — so it rests on the confirming officer's judgement.
   */
  opsConfirmCession(shortId: string): Observable<DfFacilityOps> {
    return this.http.post<DfFacilityOps>(
      `${this.base}/ops/facilities/${shortId}/confirm-cession/`,
      {},
    );
  }

  /**
   * FLOW-277 — release the declared-account hold on a facility.
   *
   * Nothing is re-scored: this lane has no automated sizing (`§A12.4`), which
   * is why the hold lands on the assessor in the first place. Releasing simply
   * lets the next approve attempt through the offer guard. `release_note` is
   * required on both lanes.
   */
  opsReleaseSizingHold(shortId: string, releaseNote: string): Observable<DfFacilityOps> {
    return this.http.post<DfFacilityOps>(
      `${this.base}/ops/facilities/${shortId}/sizing-hold/release/`,
      { release_note: releaseNote } satisfies SizingHoldReleasePayload,
    );
  }

  // -------------------------------------------------------------------------
  // Ops — invoices
  // -------------------------------------------------------------------------

  opsListInvoices(params?: { invoice_status?: string }): Observable<DfOpsInvoicesResponse> {
    let httpParams = new HttpParams();
    if (params?.invoice_status) {
      httpParams = httpParams.set('invoice_status', params.invoice_status);
    }
    return this.http.get<DfOpsInvoicesResponse>(`${this.base}/ops/invoices/`, {
      params: httpParams,
    });
  }

  opsGetInvoice(shortId: string): Observable<InvoiceOps> {
    return this.http.get<InvoiceOps>(`${this.base}/ops/invoices/${shortId}/`);
  }

  opsVerifyInvoice(shortId: string, note?: string): Observable<InvoiceOps> {
    return this.http.post<InvoiceOps>(`${this.base}/ops/invoices/${shortId}/verify/`, {
      note,
    });
  }

  opsFundInvoice(shortId: string, payload: FundInvoicePayload): Observable<InvoiceOps> {
    return this.http.post<InvoiceOps>(`${this.base}/ops/invoices/${shortId}/fund/`, payload);
  }

  opsSettleInvoice(shortId: string, note?: string): Observable<InvoiceOps> {
    return this.http.post<InvoiceOps>(`${this.base}/ops/invoices/${shortId}/settle/`, {
      note,
    });
  }

  opsRejectInvoice(shortId: string, note?: string): Observable<InvoiceOps> {
    return this.http.post<InvoiceOps>(`${this.base}/ops/invoices/${shortId}/reject/`, {
      note,
    });
  }

  // -------------------------------------------------------------------------
  // Ops — relationships
  // -------------------------------------------------------------------------

  opsGetFacilityEmails(shortId: string): Observable<OpsEmailLogEntry[]> {
    return this.http.get<OpsEmailLogEntry[]>(`${this.base}/ops/facilities/${shortId}/emails/`);
  }
}
